import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import './ForgotPasswordPage.css';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  // Step: 'email' | 'code' | 'password' | 'success'
  const [step, setStep] = useState('email');

  // Email step
  const [email, setEmail] = useState('');

  // Code step — 6 individual digit inputs
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const codeRefs = useRef([]);

  // Reset token from verify-code response
  const [resetToken, setResetToken] = useState('');

  // Password step
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Resend timer (60s countdown)
  const [resendTimer, setResendTimer] = useState(0);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [statusMsg, setStatusMsg] = useState({ type: '', message: '' });

  // Countdown timer effect
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer(prev => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // ─── Validation helpers ───────────────────────────────────────────────────

  const validateEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const passwordRules = [
    { id: 'length',    label: 'At least 8 characters',          test: (p) => p.length >= 8 },
    { id: 'upper',     label: 'One uppercase letter',            test: (p) => /[A-Z]/.test(p) },
    { id: 'lower',     label: 'One lowercase letter',            test: (p) => /[a-z]/.test(p) },
    { id: 'number',    label: 'One number',                      test: (p) => /[0-9]/.test(p) },
    { id: 'special',   label: 'One special character',           test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p) },
  ];

  const isPasswordValid = (pwd) => passwordRules.every(r => r.test(pwd));

  // ─── Step 1: Send reset code ──────────────────────────────────────────────

  const handleSendCode = async (e) => {
    e.preventDefault();
    setErrors({});
    setStatusMsg({ type: '', message: '' });

    if (!email.trim()) {
      setErrors({ email: 'Email is required' });
      return;
    }
    if (!validateEmail(email.trim())) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);
    setStatusMsg({ type: 'info', message: 'Sending verification code...' });

    try {
      await apiClient.post(API_ENDPOINTS.sendResetCode, { email: email.trim() });
      setStatusMsg({ type: '', message: '' });
      setCode(['', '', '', '', '', '']);
      setStep('code');
      setResendTimer(60);
      // Auto-focus first code box
      setTimeout(() => codeRefs.current[0]?.focus(), 100);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to send reset code. Please try again.';
      setStatusMsg({ type: 'error', message: msg });
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Step 2: Code input handlers ─────────────────────────────────────────

  const handleCodeChange = (index, value) => {
    // Only allow single numeric digit
    const digit = value.replace(/[^0-9]/g, '').slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setErrors({});

    // Auto-advance to next field
    if (digit && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (code[index]) {
        // Clear current field
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
      } else if (index > 0) {
        // Move to previous field
        codeRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      codeRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    if (!pasted) return;
    const newCode = ['', '', '', '', '', ''];
    for (let i = 0; i < pasted.length; i++) {
      newCode[i] = pasted[i];
    }
    setCode(newCode);
    // Focus the next empty field or last field
    const nextEmpty = newCode.findIndex(d => !d);
    const focusIdx = nextEmpty === -1 ? 5 : nextEmpty;
    codeRefs.current[focusIdx]?.focus();
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setErrors({});
    setStatusMsg({ type: '', message: '' });

    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setErrors({ code: 'Please enter the complete 6-digit code' });
      return;
    }

    setIsLoading(true);
    setStatusMsg({ type: 'info', message: 'Verifying code...' });

    try {
      const response = await apiClient.post(API_ENDPOINTS.verifyResetCode, {
        email: email.trim(),
        code: fullCode,
      });
      const token = response.data?.token || response.data?.data?.token || '';
      setResetToken(token);
      setStatusMsg({ type: '', message: '' });
      setStep('password');
    } catch (error) {
      const msg = error.response?.data?.message || 'Invalid or expired code. Please try again.';
      setStatusMsg({ type: 'error', message: msg });
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Resend code ──────────────────────────────────────────────────────────

  const handleResendCode = async () => {
    if (resendTimer > 0 || isLoading) return;
    setErrors({});
    setStatusMsg({ type: '', message: '' });
    setCode(['', '', '', '', '', '']);

    setIsLoading(true);
    setStatusMsg({ type: 'info', message: 'Resending code...' });

    try {
      await apiClient.post(API_ENDPOINTS.sendResetCode, { email: email.trim() });
      setStatusMsg({ type: 'success', message: 'A new code has been sent to your email.' });
      setResendTimer(60);
      setTimeout(() => codeRefs.current[0]?.focus(), 100);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to resend code. Please try again.';
      setStatusMsg({ type: 'error', message: msg });
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Step 3: Reset password ───────────────────────────────────────────────

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setErrors({});
    setStatusMsg({ type: '', message: '' });

    const newErrors = {};
    if (!newPassword) {
      newErrors.newPassword = 'Password is required';
    } else if (!isPasswordValid(newPassword)) {
      newErrors.newPassword = 'Password does not meet requirements';
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (confirmPassword !== newPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setStatusMsg({ type: 'info', message: 'Resetting your password...' });

    try {
      await apiClient.post(API_ENDPOINTS.resetPassword, {
        email: email.trim(),
        token: resetToken,
        newPassword,
      });
      setStatusMsg({ type: '', message: '' });
      setStep('success');
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to reset password. Please try again.';
      setStatusMsg({ type: 'error', message: msg });
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Back navigation ──────────────────────────────────────────────────────

  const handleBack = () => {
    setErrors({});
    setStatusMsg({ type: '', message: '' });
    if (step === 'email' || step === 'success') {
      navigate('/login');
    } else if (step === 'code') {
      setStep('email');
      setCode(['', '', '', '', '', '']);
    } else if (step === 'password') {
      setStep('code');
    }
  };

  // ─── Step indicator (3 dots for email → code → password) ─────────────────

  const stepIndex = { email: 0, code: 1, password: 2, success: 3 };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="fp-page">
      <main className="fp-main">
        <div className="fp-card">

          {/* Back button */}
          {step !== 'success' && (
            <button className="fp-back-btn" onClick={handleBack} type="button">
              <span>←</span>
              <span>Back</span>
            </button>
          )}

          {/* Step indicator dots */}
          {step !== 'success' && (
            <div className="fp-steps">
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className={`fp-step-dot ${stepIndex[step] === i ? 'active' : stepIndex[step] > i ? 'done' : ''}`}
                />
              ))}
            </div>
          )}

          {/* ── Step 1: Email ────────────────────────────────── */}
          {step === 'email' && (
            <>
              <div className="fp-icon-wrap">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <h1 className="fp-title">Forgot Password?</h1>
              <p className="fp-subtitle">Enter your email and we'll send a 6-digit verification code to reset your password.</p>

              {statusMsg.message && (
                <div className={`fp-status fp-status-${statusMsg.type}`}>{statusMsg.message}</div>
              )}

              <form onSubmit={handleSendCode} className="fp-form">
                <div className="fp-input-wrap">
                  <span className="fp-input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </span>
                  <input
                    type="email"
                    className={`fp-input ${errors.email ? 'fp-input-error' : ''}`}
                    placeholder="Email address"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setErrors({}); }}
                    disabled={isLoading}
                    autoFocus
                    autoComplete="email"
                  />
                </div>
                {errors.email && <p className="fp-field-error">{errors.email}</p>}

                <button type="submit" className="fp-btn" disabled={isLoading}>
                  {isLoading ? <span className="fp-spinner" /> : 'Send Verification Code'}
                </button>
              </form>

              <p className="fp-alt-link">
                Remember your password? <Link to="/login">Sign In</Link>
              </p>
            </>
          )}

          {/* ── Step 2: Code ─────────────────────────────────── */}
          {step === 'code' && (
            <>
              <div className="fp-icon-wrap">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <h1 className="fp-title">Enter Code</h1>
              <p className="fp-subtitle">We sent a 6-digit code to <strong>{email}</strong>. Enter it below.</p>

              {statusMsg.message && (
                <div className={`fp-status fp-status-${statusMsg.type}`}>{statusMsg.message}</div>
              )}

              <form onSubmit={handleVerifyCode} className="fp-form">
                <div className="fp-code-row" onPaste={handleCodePaste}>
                  {code.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={el => (codeRefs.current[idx] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      className={`fp-code-box ${digit ? 'filled' : ''} ${errors.code ? 'fp-code-error' : ''}`}
                      value={digit}
                      onChange={e => handleCodeChange(idx, e.target.value)}
                      onKeyDown={e => handleCodeKeyDown(idx, e)}
                      disabled={isLoading}
                      autoComplete="off"
                    />
                  ))}
                </div>
                {errors.code && <p className="fp-field-error" style={{ textAlign: 'center' }}>{errors.code}</p>}

                <button type="submit" className="fp-btn" disabled={isLoading}>
                  {isLoading ? <span className="fp-spinner" /> : 'Verify Code'}
                </button>
              </form>

              <div className="fp-resend-row">
                {resendTimer > 0 ? (
                  <span className="fp-resend-timer">Resend code in <strong>{resendTimer}s</strong></span>
                ) : (
                  <button
                    type="button"
                    className="fp-resend-btn"
                    onClick={handleResendCode}
                    disabled={isLoading}
                  >
                    Resend Code
                  </button>
                )}
              </div>
            </>
          )}

          {/* ── Step 3: New Password ──────────────────────────── */}
          {step === 'password' && (
            <>
              <div className="fp-icon-wrap">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <h1 className="fp-title">New Password</h1>
              <p className="fp-subtitle">Create a strong password for your account.</p>

              {statusMsg.message && (
                <div className={`fp-status fp-status-${statusMsg.type}`}>{statusMsg.message}</div>
              )}

              <form onSubmit={handleResetPassword} className="fp-form">
                {/* New password */}
                <div className="fp-input-wrap">
                  <span className="fp-input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={`fp-input fp-input-pw ${errors.newPassword ? 'fp-input-error' : ''}`}
                    placeholder="New password"
                    value={newPassword}
                    onChange={e => { setNewPassword(e.target.value); setErrors(prev => ({ ...prev, newPassword: '' })); }}
                    disabled={isLoading}
                    autoFocus
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="fp-eye-btn"
                    onClick={() => setShowPassword(v => !v)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.newPassword && <p className="fp-field-error">{errors.newPassword}</p>}

                {/* Confirm password */}
                <div className="fp-input-wrap">
                  <span className="fp-input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={`fp-input fp-input-pw ${errors.confirmPassword ? 'fp-input-error' : ''}`}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={e => { setConfirmPassword(e.target.value); setErrors(prev => ({ ...prev, confirmPassword: '' })); }}
                    disabled={isLoading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="fp-eye-btn"
                    onClick={() => setShowConfirmPassword(v => !v)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && <p className="fp-field-error">{errors.confirmPassword}</p>}

                {/* Password requirements checklist */}
                {newPassword.length > 0 && (
                  <div className="fp-requirements">
                    {passwordRules.map(rule => (
                      <div key={rule.id} className={`fp-req-item ${rule.test(newPassword) ? 'met' : ''}`}>
                        <span className="fp-req-icon">
                          {rule.test(newPassword) ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )}
                        </span>
                        <span>{rule.label}</span>
                      </div>
                    ))}
                  </div>
                )}

                <button type="submit" className="fp-btn" disabled={isLoading}>
                  {isLoading ? <span className="fp-spinner" /> : 'Reset Password'}
                </button>
              </form>
            </>
          )}

          {/* ── Step 4: Success ───────────────────────────────── */}
          {step === 'success' && (
            <div className="fp-success">
              <div className="fp-success-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h1 className="fp-title">Password Reset!</h1>
              <p className="fp-subtitle">
                Your password has been reset successfully. You can now sign in with your new password.
              </p>
              <button className="fp-btn" onClick={() => navigate('/login')}>
                Sign In
              </button>
            </div>
          )}

        </div>
      </main>

      <footer className="fp-footer">
        <div className="fp-footer-links">
          <Link to="/privacy">Privacy Policy</Link>
          <span>|</span>
          <Link to="/terms">Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
};

export default ForgotPasswordPage;
